// src/pages/CalendarBooking.js - Magic link booking management (supports both one-off bookings and fixed classes)
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, getDocs, doc, updateDoc, deleteDoc, getDoc, arrayUnion } from 'firebase/firestore';
import { formatTime12 } from '../components/TimePicker';
import { sendBookingEmail, sendTeacherLateCancellationEmail } from '../utils/emailService';

const SKIP_REASONS = [
  { value: 'field_trip', label: 'Field trip' },
  { value: 'testing', label: 'Testing' },
  { value: 'assembly', label: 'Assembly' },
  { value: 'other', label: 'Other' }
];

function CalendarBooking() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [bookingType, setBookingType] = useState(null); // 'booking' or 'fixedClass'
  const [userId, setUserId] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [skipDate, setSkipDate] = useState('');
  const [skipReason, setSkipReason] = useState('');
  const [skipCustomReason, setSkipCustomReason] = useState('');
  const [newNote, setNewNote] = useState('');
  const [noteDate, setNoteDate] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [upcomingDates, setUpcomingDates] = useState([]);
  const [showMoreDates, setShowMoreDates] = useState(false);

  useEffect(() => {
    loadBooking();
  }, [token]);

  const loadBooking = async () => {
    try {
      // Search through all users' bookings for this token
      const usersRef = collection(db, 'nornsWeavePublic');
      const usersSnapshot = await getDocs(usersRef);

      for (const userDoc of usersSnapshot.docs) {
        const uid = userDoc.data().userId;

        // Check approved bookings
        const bookingsRef = collection(db, 'users', uid, 'nornsWeaveBookings');
        const bookingsQuery = query(bookingsRef, where('magicLinkToken', '==', token));
        const bookingsSnapshot = await getDocs(bookingsQuery);

        if (!bookingsSnapshot.empty) {
          const bookingDoc = bookingsSnapshot.docs[0];
          setBooking({ id: bookingDoc.id, ...bookingDoc.data(), status: 'approved' });
          setBookingType('booking');
          setUserId(uid);
          await loadSettings(uid);
          setLoading(false);
          return;
        }

        // Check pending bookings
        const pendingRef = collection(db, 'users', uid, 'nornsWeavePending');
        const pendingQuery = query(pendingRef, where('magicLinkToken', '==', token));
        const pendingSnapshot = await getDocs(pendingQuery);

        if (!pendingSnapshot.empty) {
          const pendingDoc = pendingSnapshot.docs[0];
          setBooking({ id: pendingDoc.id, ...pendingDoc.data(), status: 'pending' });
          setBookingType('booking');
          setUserId(uid);
          await loadSettings(uid);
          setLoading(false);
          return;
        }

        // Check fixed classes
        const classesRef = collection(db, 'users', uid, 'nornsWeaveClasses');
        const classesQuery = query(classesRef, where('magicLinkToken', '==', token));
        const classesSnapshot = await getDocs(classesQuery);

        if (!classesSnapshot.empty) {
          const classDoc = classesSnapshot.docs[0];
          const classData = { id: classDoc.id, ...classDoc.data() };
          setBooking(classData);
          setBookingType('fixedClass');
          setUserId(uid);
          await loadSettings(uid);
          generateUpcomingDates(classData);
          setLoading(false);
          return;
        }
      }

      setError('Booking not found. This link may have expired or the booking was cancelled.');
    } catch (err) {
      console.error('Error loading booking:', err);
      setError('Error loading booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async (uid) => {
    try {
      const settingsDoc = await getDoc(doc(db, 'users', uid, 'settings', 'nornsWeave'));
      if (settingsDoc.exists()) {
        setSettings(settingsDoc.data());
      }
    } catch (err) {
      console.error('Error loading settings:', err);
    }
  };

  const generateUpcomingDates = (classData) => {
    const dates = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const dayMap = {
      'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3,
      'Thursday': 4, 'Friday': 5, 'Saturday': 6
    };

    // For "everyday" (Mon-Fri), generate next 4 weeks of weekdays
    if (classData.dayOfWeek === 'everyday') {
      let current = new Date(today);
      while (dates.length < 20) { // 4 weeks of weekdays
        const dayOfWeek = current.getDay();
        if (dayOfWeek >= 1 && dayOfWeek <= 5) { // Mon-Fri
          dates.push(new Date(current));
        }
        current.setDate(current.getDate() + 1);
      }
    } else {
      // For specific day, find next 8 occurrences
      const targetDay = dayMap[classData.dayOfWeek];
      let current = new Date(today);

      // Find next occurrence of this day
      while (current.getDay() !== targetDay) {
        current.setDate(current.getDate() + 1);
      }

      // If that day has passed this week, it's still valid for today
      if (current < today) {
        current.setDate(current.getDate() + 7);
      }

      for (let i = 0; i < 8; i++) {
        dates.push(new Date(current));
        current.setDate(current.getDate() + 7);
      }
    }

    setUpcomingDates(dates);
  };

  // Format date in local timezone to avoid UTC conversion issues
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isDateSkipped = (date) => {
    if (!booking?.exceptions) return false;
    const dateStr = formatLocalDate(date);
    return booking.exceptions.some(ex => ex.date === dateStr && ex.type === 'skipped');
  };

  const getDateNote = (date) => {
    if (!booking?.exceptions) return null;
    const dateStr = formatLocalDate(date);
    const exception = booking.exceptions.find(ex => ex.date === dateStr && ex.note);
    return exception?.note;
  };

  /**
   * Check if a cancellation is "late" and notify library chairs if so
   * @param {Object} cancelData - Cancellation details
   * @param {string} cancelData.cancelDate - The date being cancelled (YYYY-MM-DD)
   * @param {string} cancelData.teacherName - Teacher's name
   * @param {string} cancelData.grade - Grade/class
   * @param {string} cancelData.subject - Subject (optional)
   * @param {string} cancelData.classTime - Class time (e.g., "10:00 AM - 11:00 AM")
   * @param {string} cancelData.library - Library type ('elementary' or 'middle')
   */
  const checkAndNotifyLateCancellation = async (cancelData) => {
    // Check if late cancellation notifications are enabled
    if (!settings?.libraryChairSettings?.notifyOnLateTeacherCancel) {
      return;
    }

    // Get library chairs
    const libraryChairs = settings.libraryChairs || [];
    if (libraryChairs.length === 0) {
      return;
    }

    // Calculate days notice
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const classDate = new Date(cancelData.cancelDate + 'T00:00:00');
    const diffTime = classDate - today;
    const daysNotice = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Check if this is a late cancellation
    const lateNoticeDays = settings.libraryChairSettings?.lateNoticeDays || 7;
    if (daysNotice >= lateNoticeDays) {
      return; // Not a late cancellation
    }

    console.log(`Late cancellation detected: ${daysNotice} days notice (threshold: ${lateNoticeDays})`);

    // Send notification to each library chair
    for (const chair of libraryChairs) {
      if (!chair.email) continue;

      try {
        await sendTeacherLateCancellationEmail({
          chairEmail: chair.email,
          grade: cancelData.grade,
          subject: cancelData.subject || '',
          teacherName: cancelData.teacherName,
          classDate: cancelData.cancelDate,
          classTime: cancelData.classTime,
          library: cancelData.library,
          daysNotice: daysNotice,
          librarianName: settings.yourName,
          schoolName: settings.schoolName,
          librarianEmail: settings.librarianEmail,
        });
        console.log(`Late cancellation notification sent to ${chair.email}`);
      } catch (error) {
        console.error(`Failed to send late cancellation email to ${chair.email}:`, error);
      }
    }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      if (bookingType === 'booking') {
        const collectionName = booking.status === 'approved' ? 'nornsWeaveBookings' : 'nornsWeavePending';

        // Check for late cancellation before deleting (only for approved bookings)
        if (booking.status === 'approved') {
          await checkAndNotifyLateCancellation({
            cancelDate: booking.date,
            teacherName: booking.teacherName,
            grade: booking.grade,
            subject: booking.subject || booking.purpose,
            classTime: `${formatTime12(booking.startTime)} - ${formatTime12(booking.endTime)}`,
            library: booking.library,
          });
        }

        await deleteDoc(doc(db, 'users', userId, collectionName, booking.id));
      } else {
        // For fixed classes, we don't delete - just mark as cancelled
        await updateDoc(doc(db, 'users', userId, 'nornsWeaveClasses', booking.id), {
          status: 'cancelled',
          cancelledAt: new Date().toISOString()
        });
      }

      alert('Booking cancelled successfully.');
      navigate('/calendar');
    } catch (err) {
      console.error('Error cancelling booking:', err);
      alert('Error cancelling booking. Please try again.');
    } finally {
      setActionLoading(false);
      setShowCancelConfirm(false);
    }
  };

  const handleSkipWeek = async () => {
    if (!skipDate) return;

    setActionLoading(true);
    try {
      const reasonText = skipReason === 'other' ? skipCustomReason :
        SKIP_REASONS.find(r => r.value === skipReason)?.label || 'No reason provided';

      await updateDoc(doc(db, 'users', userId, 'nornsWeaveClasses', booking.id), {
        exceptions: arrayUnion({
          date: skipDate,
          type: 'skipped',
          reason: reasonText,
          markedAt: new Date().toISOString()
        })
      });

      // Send notification to librarian
      if (settings?.emailEnabled && settings?.librarianEmail) {
        try {
          const skipDateFormatted = new Date(skipDate).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });

          await sendBookingEmail({
            type: 'week_skipped_librarian',
            to: settings.librarianEmail,
            teacherName: booking.teacherName,
            bookingDate: skipDate,
            bookingTime: `${formatTime12(booking.startTime)} - ${formatTime12(booking.endTime)}`,
            library: booking.library,
            grade: booking.grade,
            schoolName: settings?.schoolName,
            skipDate: skipDateFormatted,
            skipReason: reasonText,
            replyTo: booking.teacherEmail
          });
        } catch (emailErr) {
          console.error('Error sending skip notification:', emailErr);
        }
      }

      // Check for late cancellation and notify library chairs if needed
      await checkAndNotifyLateCancellation({
        cancelDate: skipDate,
        teacherName: booking.teacherName,
        grade: booking.grade,
        subject: booking.subject,
        classTime: `${formatTime12(booking.startTime)} - ${formatTime12(booking.endTime)}`,
        library: booking.library,
      });

      // Reload booking to get updated exceptions
      await loadBooking();
      setShowSkipModal(false);
      setSkipDate('');
      setSkipReason('');
      setSkipCustomReason('');
      alert('This week has been marked as skipped. Your librarian has been notified.');
    } catch (err) {
      console.error('Error skipping week:', err);
      alert('Error skipping week. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    setActionLoading(true);
    try {
      if (bookingType === 'booking') {
        const collectionName = booking.status === 'approved' ? 'nornsWeaveBookings' : 'nornsWeavePending';
        const existingNotes = booking.notes || '';
        const updatedNotes = existingNotes
          ? `${existingNotes}\n\n[${new Date().toLocaleDateString()}] ${newNote}`
          : `[${new Date().toLocaleDateString()}] ${newNote}`;

        await updateDoc(doc(db, 'users', userId, collectionName, booking.id), {
          notes: updatedNotes
        });

        setBooking({ ...booking, notes: updatedNotes });
      } else {
        // For fixed classes, add note to specific date
        const targetDate = noteDate || formatLocalDate(new Date());
        await updateDoc(doc(db, 'users', userId, 'nornsWeaveClasses', booking.id), {
          exceptions: arrayUnion({
            date: targetDate,
            type: 'note',
            note: newNote,
            markedAt: new Date().toISOString()
          })
        });
        await loadBooking();
      }

      setNewNote('');
      setNoteDate('');
      setShowAddNote(false);
      alert('Note added successfully.');
    } catch (err) {
      console.error('Error adding note:', err);
      alert('Error adding note. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    // Parse as local date to avoid timezone issues
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateShort = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const getScheduleText = () => {
    if (!booking) return '';
    if (booking.dayOfWeek === 'everyday') {
      return 'Every school day (Mon-Fri)';
    }
    return `Every ${booking.dayOfWeek}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center border border-red-200">
          <div className="text-6xl mb-4">😕</div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Booking Not Found</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/calendar')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Go to Calendar
          </button>
        </div>
      </div>
    );
  }

  // Fixed Class UI
  if (bookingType === 'fixedClass') {
    const displayedDates = showMoreDates ? upcomingDates : upcomingDates.slice(0, 4);

    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-cyan-50 p-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-cyan-100">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="text-4xl">📚</div>
              <div>
                <h1 className="text-2xl font-bold text-slate-800">Your Library Class Schedule</h1>
                <p className="text-slate-500">{booking.teacherName}</p>
              </div>
            </div>

            {/* Schedule Info */}
            <div className="bg-cyan-50 rounded-xl p-6 mb-6 border-2 border-cyan-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-cyan-600 font-medium mb-1">Schedule</p>
                  <p className="text-lg font-bold text-slate-800">{getScheduleText()}</p>
                </div>
                <div>
                  <p className="text-sm text-cyan-600 font-medium mb-1">Time</p>
                  <p className="text-lg font-bold text-slate-800">
                    {formatTime12(booking.startTime)} - {formatTime12(booking.endTime)}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <div>
                  <p className="text-sm text-cyan-600 font-medium mb-1">Library</p>
                  <p className="font-medium text-slate-800">
                    {booking.library === 'elementary' ? 'Elementary Library' : 'Middle School Library'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-cyan-600 font-medium mb-1">Grade/Class</p>
                  <p className="font-medium text-slate-800">
                    {booking.grade}{booking.subject ? ` - ${booking.subject}` : ''}
                  </p>
                </div>
              </div>
            </div>

            {/* Upcoming Classes */}
            <div className="mb-6">
              <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
                <span>📅</span> Upcoming Classes
              </h2>
              <div className="space-y-2">
                {displayedDates.map((date, index) => {
                  const dateStr = formatLocalDate(date);
                  const isSkipped = isDateSkipped(date);
                  const note = getDateNote(date);

                  return (
                    <div
                      key={index}
                      className={`rounded-lg p-4 border ${
                        isSkipped
                          ? 'bg-slate-100 border-slate-300'
                          : 'bg-white border-slate-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`font-medium ${isSkipped ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                            {formatDateShort(date)}
                          </p>
                          <p className={`text-sm ${isSkipped ? 'text-slate-400' : 'text-slate-500'}`}>
                            {formatTime12(booking.startTime)} - {formatTime12(booking.endTime)}
                          </p>
                          {isSkipped && (
                            <p className="text-sm text-red-500 mt-1">Skipped</p>
                          )}
                          {note && (
                            <p className="text-sm text-blue-600 mt-1">📝 {note}</p>
                          )}
                        </div>
                        {!isSkipped && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setSkipDate(dateStr);
                                setShowSkipModal(true);
                              }}
                              className="px-3 py-1 text-sm bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors"
                            >
                              Skip
                            </button>
                            <button
                              onClick={() => {
                                setNoteDate(dateStr);
                                setShowAddNote(true);
                              }}
                              className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                            >
                              Add Note
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {upcomingDates.length > 4 && (
                <button
                  onClick={() => setShowMoreDates(!showMoreDates)}
                  className="mt-3 text-cyan-600 hover:text-cyan-700 text-sm font-medium"
                >
                  {showMoreDates ? 'Show less' : `Show more dates (${upcomingDates.length - 4} more)`}
                </button>
              )}
            </div>

            {/* Actions */}
            <div className="border-t border-slate-200 pt-6">
              <div className="bg-amber-50 rounded-lg p-4 mb-4 border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>Need to change your regular day or time?</strong><br/>
                  Contact {settings?.yourName || 'your librarian'} to update your recurring schedule.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/calendar')}
                  className="flex-1 bg-cyan-100 text-cyan-700 py-3 rounded-lg hover:bg-cyan-200 transition-colors font-medium"
                >
                  Book Extra Visit
                </button>
                <button
                  onClick={() => setShowCancelConfirm(true)}
                  className="flex-1 bg-red-100 text-red-700 py-3 rounded-lg hover:bg-red-200 transition-colors font-medium"
                >
                  Cancel All Classes
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Skip Week Modal */}
        {showSkipModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-slate-800 mb-4">Skip Library Class</h3>
              <p className="text-slate-600 mb-4">
                You're skipping your library visit on:<br/>
                <strong>{formatDate(skipDate)}</strong>
              </p>

              <div className="mb-4">
                <label className="block text-slate-700 text-sm font-medium mb-2">Reason (optional)</label>
                <div className="space-y-2">
                  {SKIP_REASONS.map(reason => (
                    <label key={reason.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="skipReason"
                        value={reason.value}
                        checked={skipReason === reason.value}
                        onChange={(e) => setSkipReason(e.target.value)}
                        className="text-cyan-600"
                      />
                      <span className="text-slate-700">{reason.label}</span>
                    </label>
                  ))}
                </div>
                {skipReason === 'other' && (
                  <input
                    type="text"
                    placeholder="Enter reason..."
                    value={skipCustomReason}
                    onChange={(e) => setSkipCustomReason(e.target.value)}
                    className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-lg focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none"
                  />
                )}
              </div>

              <p className="text-sm text-slate-500 mb-4">Your librarian will be notified.</p>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowSkipModal(false);
                    setSkipDate('');
                    setSkipReason('');
                    setSkipCustomReason('');
                  }}
                  className="flex-1 bg-slate-200 text-slate-700 py-2 rounded-lg hover:bg-slate-300 transition-colors"
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSkipWeek}
                  disabled={actionLoading}
                  className="flex-1 bg-amber-500 text-white py-2 rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50"
                >
                  {actionLoading ? 'Skipping...' : 'Confirm Skip'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cancel All Confirmation */}
        {showCancelConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-slate-800 mb-4">Cancel All Classes?</h3>
              <p className="text-slate-600 mb-6">
                This will cancel your recurring library schedule. To book again, you'll need to contact {settings?.yourName || 'your librarian'}.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  className="flex-1 bg-slate-200 text-slate-700 py-2 rounded-lg hover:bg-slate-300 transition-colors"
                  disabled={actionLoading}
                >
                  Keep Schedule
                </button>
                <button
                  onClick={handleCancel}
                  disabled={actionLoading}
                  className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {actionLoading ? 'Cancelling...' : 'Yes, Cancel All'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Note Modal */}
        {showAddNote && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Add a Note {noteDate && `for ${formatDate(noteDate)}`}
              </h3>
              <textarea
                className="w-full px-3 py-2 border-2 border-cyan-200 rounded-lg focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none"
                rows="4"
                placeholder="Add any notes for your librarian..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
              />
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => { setShowAddNote(false); setNewNote(''); setNoteDate(''); }}
                  className="flex-1 bg-slate-200 text-slate-700 py-2 rounded-lg hover:bg-slate-300 transition-colors"
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddNote}
                  disabled={actionLoading || !newNote.trim()}
                  className="flex-1 bg-cyan-600 text-white py-2 rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50"
                >
                  {actionLoading ? 'Adding...' : 'Add Note'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Regular Booking UI (existing code, cleaned up)
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 p-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-blue-100">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-slate-800">Booking Details</h1>
            <span className={`px-4 py-2 rounded-full text-sm font-bold ${
              booking.status === 'approved'
                ? 'bg-green-100 text-green-700 border-2 border-green-300'
                : booking.status === 'pending'
                ? 'bg-yellow-100 text-yellow-700 border-2 border-yellow-300'
                : 'bg-red-100 text-red-700 border-2 border-red-300'
            }`}>
              {booking.status === 'approved' ? '✓ Confirmed' :
               booking.status === 'pending' ? '⏳ Awaiting Approval' :
               '✗ ' + booking.status}
            </span>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-600 font-medium mb-1">Date</p>
                <p className="text-lg font-bold text-slate-800">{formatDate(booking.date)}</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-600 font-medium mb-1">Time</p>
                <p className="text-lg font-bold text-slate-800">
                  {formatTime12(booking.startTime)} - {formatTime12(booking.endTime)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-500 mb-1">Library</p>
                <p className="font-medium text-slate-800">
                  {booking.library === 'elementary' ? '📚 Elementary Library' : '📖 Middle School Library'}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Type</p>
                <p className="font-medium text-slate-800">
                  {booking.type === 'admin' ? '🏢 Admin Use' : '👨‍🏫 Class Visit'}
                </p>
              </div>
            </div>

            {booking.type !== 'admin' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Grade/Class</p>
                  <p className="font-medium text-slate-800">{booking.grade}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Students</p>
                  <p className="font-medium text-slate-800">{booking.studentCount}</p>
                </div>
              </div>
            )}

            <div>
              <p className="text-sm text-slate-500 mb-1">Teacher</p>
              <p className="font-medium text-slate-800">{booking.teacherName}</p>
              <p className="text-sm text-slate-600">{booking.teacherEmail}</p>
            </div>

            {booking.needsLibrarian && (
              <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
                <p className="text-sm text-purple-600 font-medium mb-1">👤 Librarian Requested</p>
                {booking.lessonRequest && (
                  <p className="text-slate-700">{booking.lessonRequest}</p>
                )}
              </div>
            )}

            {booking.services && Object.values(booking.services).some(v => v) && (
              <div>
                <p className="text-sm text-slate-500 mb-2">Services Requested</p>
                <div className="space-y-1">
                  {booking.services.researchSupport && (
                    <p className="text-sm text-slate-700">📚 Research support</p>
                  )}
                  {booking.services.bookCheckout && (
                    <p className="text-sm text-slate-700">📖 Book checkout at end</p>
                  )}
                  {booking.services.projectorScreen && (
                    <p className="text-sm text-slate-700">📽️ Projector/screen</p>
                  )}
                  {booking.services.bookSetsNeeded && (
                    <p className="text-sm text-slate-700">📚 Book sets: {booking.services.bookSetsDetails}</p>
                  )}
                  {booking.services.aideHelpRequested && (
                    <p className="text-sm text-slate-700">🙋 Aide help: {booking.services.aideHelpDetails}</p>
                  )}
                  {booking.services.other && (
                    <p className="text-sm text-slate-700">📝 Other: {booking.services.otherDetails}</p>
                  )}
                </div>
              </div>
            )}

            {booking.purpose && (
              <div>
                <p className="text-sm text-slate-500 mb-1">Purpose</p>
                <p className="text-slate-700">{booking.purpose}</p>
              </div>
            )}

            {booking.notes && (
              <div>
                <p className="text-sm text-slate-500 mb-1">Notes</p>
                <p className="text-slate-700 whitespace-pre-wrap">{booking.notes}</p>
              </div>
            )}

            {booking.rejectionReason && (
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-600 font-medium mb-1">Reason for Decline</p>
                <p className="text-slate-700">{booking.rejectionReason}</p>
                {booking.suggestedAlternatives && (
                  <>
                    <p className="text-sm text-red-600 font-medium mt-3 mb-1">Suggested Alternatives</p>
                    <p className="text-slate-700">{booking.suggestedAlternatives}</p>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="mt-8 flex gap-3">
            <button
              onClick={() => navigate('/calendar')}
              className="flex-1 bg-slate-100 text-slate-700 py-3 rounded-lg hover:bg-slate-200 transition-colors font-medium"
            >
              Back to Calendar
            </button>
            {booking.status !== 'rejected' && (
              <>
                <button
                  onClick={() => setShowAddNote(true)}
                  className="flex-1 bg-blue-100 text-blue-700 py-3 rounded-lg hover:bg-blue-200 transition-colors font-medium"
                >
                  Add Note
                </button>
                <button
                  onClick={() => setShowCancelConfirm(true)}
                  className="flex-1 bg-red-100 text-red-700 py-3 rounded-lg hover:bg-red-200 transition-colors font-medium"
                >
                  Cancel Booking
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-slate-800 mb-4">Cancel Booking?</h3>
            <p className="text-slate-600 mb-6">
              Are you sure you want to cancel this booking? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="flex-1 bg-slate-200 text-slate-700 py-2 rounded-lg hover:bg-slate-300 transition-colors"
                disabled={actionLoading}
              >
                Keep Booking
              </button>
              <button
                onClick={handleCancel}
                disabled={actionLoading}
                className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Cancelling...' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddNote && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-slate-800 mb-4">Add a Note</h3>
            <textarea
              className="w-full px-3 py-2 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
              rows="4"
              placeholder="Add any additional information..."
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => { setShowAddNote(false); setNewNote(''); }}
                className="flex-1 bg-slate-200 text-slate-700 py-2 rounded-lg hover:bg-slate-300 transition-colors"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleAddNote}
                disabled={actionLoading || !newNote.trim()}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Adding...' : 'Add Note'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendarBooking;
